import { type FunctionComponent, type ReactNode, useState } from 'react';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { useNavigation } from '../../../hooks/navigation/navigation.hooks';
import { ClickableElement } from '../../buttons';
import ChevronDownIcon from '../../svgs/untitled-ui/chevron-down.svg';
import ChevronUpIcon from '../../svgs/untitled-ui/chevron-up.svg';
import { Badge } from '../badge/badge.component';

export type SectionItemProps = {
	name: string;
	description: string;
	url?: string;
	badgeCount?: number;
	expandableContent?: ReactNode | ReactNode[];
	onSelectSection?: () => void;
	onCollapse?: () => Promise<void>;
};

export const SectionItem: FunctionComponent<SectionItemProps> = ({
	name,
	description,
	url,
	badgeCount,
	expandableContent,
	onSelectSection,
	onCollapse
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const navigate = useNavigation();

	const handleClick = async () => {
		if (expandableContent) {
			setIsOpen(!isOpen);
			if (isOpen && onCollapse) {
				await onCollapse();
			}
		} else if (url) {
			navigate(url);
		}
		if (onSelectSection) {
			onSelectSection();
		}
	};

	return (
		<div
			className={`flex relative lh-title flex-column pa3 bg-theme-white br2 shadow-1 ${
				isOpen ? ' bg-theme-grey-lighter' : 'hover-bg-theme-grey-lighter'
			}`}>
			<ClickableElement
				onClick={handleClick}
				onKeyDown={handleKeys(['Enter', ' '], handleClick)}
				ariaLabel="section item"
				className={`flex justify-between items-center pointer ${isOpen ? 'pb3' : ''}`}>
				<>
					<div>
						<div className="f6 font-medium mw5 truncate">{name}</div>
						<span className="f7">{description}</span>
					</div>
					<div className="flex">
						{badgeCount && badgeCount > 0 ? <Badge count={badgeCount} noMargin={true} /> : null}
						{expandableContent ? (
							<div className="f6 theme-grey ml2 flex items-center">
								{isOpen ? (
									<ChevronUpIcon className="text-2xl" data-testid="ChevronUpIcon" />
								) : (
									<ChevronDownIcon className="text-2xl" data-testid="ChevronDownIcon" />
								)}
							</div>
						) : null}
					</div>
				</>
			</ClickableElement>
			{isOpen && expandableContent}
		</div>
	);
};

export type SectionListContainerProps = {
	name: string;
	children: ReactNode | ReactNode[];
	startOpen?: boolean;
	badgeCount?: number;
	containerClass?: string;
	childContainerClass?: string;
};

export const SectionListContainer: FunctionComponent<SectionListContainerProps> = ({
	name,
	children,
	startOpen = true,
	badgeCount,
	containerClass = '',
	childContainerClass
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(startOpen);
	return (
		<div className={containerClass}>
			<ClickableElement
				onClick={() => setIsOpen(!isOpen)}
				onKeyDown={handleKeys(['Enter', ' '], () => setIsOpen(!isOpen))}
				ariaLabel="section container"
				className={`justify-between bg-theme-grey-lighter br2 pt3 ph4 f5 font-medium ${isOpen ? 'br--top' : 'mb1 pb3'}`}>
				<div className="flex justify-between">
					{name}
					<div className="flex items-center">
						{badgeCount && badgeCount > 0 ? <Badge count={badgeCount} noMargin={true} /> : null}
						<span className="f5 theme-grey ml2 mr1 right-1">
							{isOpen ? (
								<ChevronUpIcon data-testid="ChevronUpIcon" className="text-2xl" />
							) : (
								<ChevronDownIcon data-testid="ChevronDownIcon" className="text-2xl" />
							)}
						</span>
					</div>
				</div>
			</ClickableElement>
			{isOpen && (
				<div className={`pa3 flex flex-column bg-theme-grey-lighter br2 br--bottom ${childContainerClass}`} style={{ gap: '10px' }}>
					{children}
				</div>
			)}
		</div>
	);
};
